import React, { useMemo } from 'react';
import { Row, Col, CustomInput, Input, Label, FormFeedback } from 'reactstrap';
import { connect } from 'formik';
import { useTranslation } from 'react-i18next';

import Tooltip from '#/Components/Presentational/Tooltip/Tooltip';
import SVGIcon from '#/Components/Presentational/SVGIcon/SVGIcon';
import { RecipientPickupType } from '../types';
import { ServiceArgument, getArgumentFieldName, getInputArguments } from './argumentInputs';

import css from './ExtraServices.module.scss';

interface Values {
  // service code => checkbox state, argument field name => typed value
  [key: string]: boolean | string;
}

interface Service {
  id: string;
  code: string;
  name: string;
  description: string;
  selectedByDefault: boolean;
  arguments: ServiceArgument[];
  allowedForApi: boolean;
  allowedForApp: boolean;
  allowedForCsv: boolean;
}

type Props = {
  services: Service[];
  collectionPlaces: any[];
  recipientCollectionPlace: any;
  recipientPickupType: RecipientPickupType;
  agentAbbr: string;
};

const ExtraServices = connect<Props, Values>(({
  services,
  collectionPlaces,
  recipientCollectionPlace,
  recipientPickupType,
  agentAbbr,
  formik,
}) => {
  const { t } = useTranslation(['services']);
  const { values, handleChange, errors, touched } = formik;
  const availableExtraServices = useMemo(() => {
    return services.filter((es) => es.allowedForApp);
  }, [services]);

  const selectedCollectionPlace = collectionPlaces.find(
    (collectionPlace: any) => collectionPlace.id === recipientCollectionPlace,
  );

  const getHintValue = (type: 'phone' | 'email'): string => {
    if (type === 'phone') {
      return recipientPickupType === 'collectionPlace' ? selectedCollectionPlace.contact.phone : values.recipientPhone;
    }

    if (type === 'email') {
      return recipientPickupType === 'collectionPlace' ? selectedCollectionPlace.contact.email : values.recipientEmail;
    }

    return '';
  };

  const getHintFromService = (service: Service, type: 'phone' | 'email'): string => (
    service.arguments.some((argument) => argument.type === type)
      ? getHintValue(type)
      : ''
  );

  const renderArgumentInput = (service: Service, argument: ServiceArgument) => {
    const fieldName = getArgumentFieldName(service.code, argument.type);
    const hasError = Boolean(touched[fieldName] && errors[fieldName]);

    return (
      <div className={css.argument} key={fieldName}>
        <Label for={fieldName} className="mb-1">{argument.name}</Label>
        <Input
          type="text"
          id={fieldName}
          name={fieldName}
          value={String(values[fieldName] ?? '')}
          placeholder={argument.exampleValue}
          onChange={handleChange}
          invalid={hasError}
        />
        {hasError && <FormFeedback>{errors[fieldName]}</FormFeedback>}
      </div>
    );
  };

  const rows = availableExtraServices.map((service, index) => {
    const hint = (
      getHintFromService(service, 'phone') || getHintFromService(service, 'email')
    );
    const argumentInputs = values[service.code] ? getInputArguments(service) : [];

    return (
      <Row
        className={css.extraService}
        key={service.id}
      >
        <Col md="7">
          <CustomInput
            type="checkbox"
            id={service.code}
            label={t([`services:${agentAbbr}.${service.code}`, `services:${service.code}.name`], service.name)}
            className={service.description && css.hasDescription}
            onChange={handleChange}
            checked={Boolean(values[service.code])}
          />

          {service.description && (
            <Tooltip
              id={`tooltip-email-${index}`}
              linkText={(
                <SVGIcon
                  icon="help"
                  style={{
                    maxHeight: '24px',
                    maxWidth: '24px',
                  }}
                />
              )}
              text={t(`services:${service.code}.description`)}
              linkIconHide
              linkIcon={[]}
              linkIconPosition="after"
              placement="right"
            />
          )}

          {argumentInputs.map((argument) => renderArgumentInput(service, argument))}
        </Col>

        <Col md="5">
          {hint}
        </Col>
      </Row>
    );
  });

  return (
    <>
      {rows}
    </>
  );
});

export default ExtraServices;
